import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import './play-game.styles.scss';
import {
  useNavigate,
  useParams
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getGameDataFromDb,
  getPlayerDataFromDb,
  getUniqueDocumentFromDb,
  getLatestGameMessageFromDb,
  getGameElementShortList
} from '@/utils/getDataFromDb';
import PlayerGameContent from '@/components/PLAYER/player-game-content.component';
import {
  IsDesktop,
  IsMobile,
  IsTablet
} from '@/utils/responsiveQueries';
import PlayHeadBar from '@/components/PLAYER/play-game-head-bar/play-head-bar.component';
import Loader from '@/components/loader/loader.comp';
import {
  UseCloudFunction,
  auth
} from '@/utils/firebase.utils';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { off } from 'firebase/database';
import PlayHeadBarMobile from '@/components/PLAYER/play-game-head-bar/play-head-mobile.component';
import LegalApprovalModal from '@/components/SHARED/legal-approval-modal/legal-approval-modal.component';
import { checkForEarnedLevels } from '@/utils/checkForEarnedLevels';
import { getQueryFromFirestore, getQueryFromRtDb } from '@/utils/getDataFromDbSimplified';
import AnnouncementModal from '@/components/SHARED/announcements/announcement-modal.component';

const PlayGamePage = () => {

  const userProfile = useSelector(state => state.userData.userProfile);
  // Keep latest profile accessible inside delayed callbacks without re-running effect setup.
  const userProfileRef = useRef(userProfile);
  const authCheckTimeoutRef = useRef(null);
  const playerDataRetryTimeoutRef = useRef(null);
  const [gameData, setGameData] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [gameElements, setGameElements] = useState(null);
  const [playerActivity, setPlayerActivity] = useState(null);
  const [actionItems, setActionItems] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [playerList, setPlayerList] = useState(null);
  const [teamMembers, setTeamMembers] = useState({});
  const [myTeams, setMyTeams] = useState(null);
  const [latestMessage, setLatestMessage] = useState(null);
  const [loadError, setLoadError] = useState(null); // set when the game or player read fails, so we stop spinning
  const [unadjustedPlayerData, setUnadjustedPlayerData] = useState({}); // for debugging - playerData with newStuff adjustments reversed out
  const { gamePath } = useParams();
  const isDesktop = IsDesktop();
  const isTablet = IsTablet();
  const isMobile = IsMobile();
  // Tablet and mobile share the compact head bar; desktop and largeTablet share the full one.
  const useMobileHeadBar = isTablet || isMobile;
  const playerDataRef = useRef(); // to hold latest playerData for level check
  const elementsRef = useRef();
  const actionItemsRef = useRef();
  const playerListRef = useRef();
  const navigate = useNavigate();
  const playerListenerRef = useRef();
  const elementsListenerRef = useRef();
  const checkVisIntervalRef = useRef();
  const actionItemsListenerRef = useRef();
  const statsListenerRef = useRef();
  const playerListListenerRef = useRef();
  const teamListenerRef = useRef();
  const messageListenerRef = useRef();

  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile])

  useEffect(() => {
    if (!userProfile) return;

    // checkElementVisibility();
    checkForAuth();
    checkVisIntervalRef.current = setInterval(() => {
      // checkElementVisibility();
    }, 100000)

    function checkForAuth(attempt = 0) {
      const currentlySignedInUser = auth.currentUser;
      const appId = userProfileRef.current?.appId;
      if (currentlySignedInUser && appId) {
        getGameData();
        return;
      }
      // First load can race Firebase auth + profile hydration; retry briefly before logout.
      if (attempt >= 10) {
        console.log('logging out: missing auth or appId');
        navigate('/logout');
        return;
      }
      authCheckTimeoutRef.current = setTimeout(() => {
        checkForAuth(attempt + 1);
      }, 500);
    }

    function getGameData() {
      getGameDataFromDb({
        'gamePath': gamePath,
        'status': 'active',
        'once': true,
        'callback': callback,
        'onError': onError
      })
      function callback(dataObj) {
        const data = Object.values(dataObj ?? {})[0];
        // A one-shot read that comes back empty used to throw on data.gameId inside
        // the async util, where the rejection was swallowed and the page spun forever.
        if (!data) {
          console.log('no active game found for path:', gamePath);
          setLoadError('We could not find that game. It may have been removed, or you may not have access to it.');
          return;
        }
        setGameData(data);
        setGameId(data.gameId);
        changeDocumentTitle(`Gamably | ${data.gameName}`, data.icon);
        // change var(--primary) to game color
        var r = document.querySelector(':root');
        r.style.setProperty('--primary', data.color);
        r.style.setProperty('--primary-trans', data.color + 'a6');
      }
      function onError(err) {
        setLoadError(`We could not load this game. (${err?.code ?? err?.message ?? 'unknown error'})`);
      }
    }

    return () => {
      if (authCheckTimeoutRef.current) clearTimeout(authCheckTimeoutRef.current);
      if (checkVisIntervalRef.current) clearInterval(checkVisIntervalRef.current);
      
    }
  }, [userProfile?.appId])

  useEffect(() => {
    if (!gameId) return;

    function getPlayerDataWhenReady(gameId, attempt = 0) {
      const appId = userProfileRef.current?.appId;
      if (appId) {
        getPlayerData(gameId, appId);
        return;
      }
      // Never query players without appId: getPlayerDataFromDb would otherwise return all players.
      if (attempt >= 10) {
        console.log('logging out: missing userProfile.appId');
        navigate('/logout');
        return;
      }
      playerDataRetryTimeoutRef.current = setTimeout(() => {
        getPlayerDataWhenReady(gameId, attempt + 1);
      }, 300);
    }

    function getPlayerData(gameId, appId) {
      getPlayerDataFromDb({
        'gameId': gameId,
        'appId': appId,
        'callback': callback,
        'handleListener': handleListener,
        'onError': onError
      })
      function onError(err) {
        setLoadError(`We could not load your player profile for this game. (${err?.code ?? err?.message ?? 'unknown error'})`);
      }
      function callback(data) {
        console.log(data);
        if (data.status !== 'active') {
          navigate('/logout');
          return;
        }
        if (data.signout) {
          signPlayerOut(data.playerId);
          return;
        }
        const processedUser = processNewStuff(data);
        setPlayerData(processedUser);
      }
      function handleListener(unsub) {
        playerListenerRef.current = unsub;
      }
    }

    function getGameElements(gameId) {
      getGameElementShortList({
        'gameId': gameId,
        'callback': callback,
        'handleListener': handleListener
      })
      function callback(data) {
        // console.log('element update')
        // console.log(data);
        const now = new Date().getTime();
        let obj = {};
        for (let el of Object.values(data)) {
          if (
            el.status !== 'active' ||
            el.type === 'secretCode' ||
            el.type === 'momentum'
          ) {
            // delete elements[el.id];
            continue;
          }
          obj[el.id] = el;
        }
        // console.log(obj);
        elementsRef.current = { ...obj };
        setGameElements(elementsRef.current);
        // checkElementVisibility();
      }
      function handleListener(unsub) {
        elementsListenerRef.current = unsub;
      }
    }

    function getGameStats(gameId) {
      getUniqueDocumentFromDb({
        'collection': `games/${gameId}/gameStats`,
        'docId': `${gameId}-gameStats`,
        'callback': callback,
        'handleListener': handleListener
      })
      function callback(data) {
        setGameStats(data);
      }
      function handleListener(unsub) {
        statsListenerRef.current = unsub;
      }
    }

    function getLeaderList(gameId) {
      getQueryFromRtDb({
        'path': `games/${gameId}/leaderList`,
        'callback': callback,
        'handleListener': handleListener
      })
      function callback(data) {
        // console.log(data);
        playerListRef.current = {
          ...playerListRef.current,
          ...data
        }
        let playerObj = {}
        for (let p of Object.values(playerListRef.current)) {
          if (
            !p.team
          ) {
            playerObj[p.playerId] = p
          }
        }
        setPlayerList(playerObj)
      }
      function handleListener(unsub) {
        playerListListenerRef.current = unsub;
      }
    }

    async function getLatestMessage(gameId) {
      getLatestGameMessageFromDb({
        'gameId': gameId,
        'callback': callback,
        'handleListener': handleListener
      })
      function callback(data) {
        setLatestMessage(data)
      }
      function handleListener(unsub) {
        messageListenerRef.current = unsub;
      }
    }

    getPlayerDataWhenReady(gameId);
    getGameElements(gameId);
    getGameStats(gameId);
    getLeaderList(gameId);
    getLatestMessage(gameId);

    return () => {
      if (playerDataRetryTimeoutRef.current) clearTimeout(playerDataRetryTimeoutRef.current);
      if (playerListenerRef.current) playerListenerRef.current();
      if (playerListListenerRef.current) off(playerListListenerRef.current);
      if (elementsListenerRef.current) elementsListenerRef.current();
      if (statsListenerRef.current) statsListenerRef.current();
      if (messageListenerRef.current) messageListenerRef.current();
    }
  }, [gameId])

  useEffect(() => {
    if (!gameId || !playerData?.playerId) return;

    function getActionItems(gameId, playerId) {
      getQueryFromFirestore({
        'path': `games/${gameId}/actionItems`,
        'queryArray': [
          {
            'field': 'playerId',
            'operator': '==',
            'value': playerId
          }
        ],
        'callback': callback,
        'handleListener': handleListener

      })
      function callback(data) {
        actionItemsRef.current = { ...actionItemsRef.current, ...{ [data.id]: data } }
        setActionItems(actionItemsRef.current);
      }
      function handleListener(unsub) {
        actionItemsListenerRef.current = unsub
      }
    }

    function getTeamMembers(gameId, playerId) {
      getUniqueDocumentFromDb({
        "collection": `games/${gameId}/teamMembers`,
        "docId": `${gameId}-teamMembers`,
        "callback": callback,
        "handleListener": handleListener
      })
      function callback(data) {
        if (!data) return;
        setTeamMembers(data);
        let myTeamArr = [];
        for (let team of Object.values(data)) {
          if (team.members && team.members.includes(playerId)) {
            myTeamArr.push(team.path);
          }
        }
        setMyTeams([...myTeamArr]);
      }
      function handleListener(unsub) {
        teamListenerRef.current = unsub;
      }
    }

    getActionItems(gameId, playerData.playerId);
    getTeamMembers(gameId, playerData.playerId);

    return () => {
      if (actionItemsListenerRef.current) actionItemsListenerRef.current();
      if (teamListenerRef.current) teamListenerRef.current();
    }
  }, [gameId, playerData?.playerId])


  useEffect(() => {
    if (!playerData || !gameElements || !gameData) return;
    // if (alreadyChecked.current) return;
    // checkForEarnedLevels();
    if (playerDataRef.current?.points !== playerData.points) {
      checkForEarnedLevels(gameElements, playerData, gameData);
    }
    playerDataRef.current = { ...playerData }; // keep ref up to date
  }, [playerData, gameElements, gameData])

  async function signPlayerOut(playerId) {
    const res = await UseCloudFunction('resetPlayerSignOut', { 'playerId': playerId })
    console.log(res);
    navigate('/logout');
  }

  function processNewStuff(playerData) {
    const tempPlayer = { ...playerData }
    // save unadjusted player so they can't use currency they lost 
    // or 
    setUnadjustedPlayerData(tempPlayer);
    if (!playerData.newStuff) return tempPlayer;
    // go through elements in playerData.newStuff and remove
    // items, points, etc. from playerData
    for (let stuff of tempPlayer.newStuff) {
      if (!stuff.data) continue;
      if (stuff.type === 'momentum' && stuff.spec === 'negative') {
        if (stuff.data) {
          for (let d of stuff.data) {
            if (d.type === 'xpRemoved' && tempPlayer.points > 0) {
              tempPlayer.points += d.quantity;
            }
            else if (d.type === 'currencyRemoved' && tempPlayer.currency > 0) {
              tempPlayer.currency += d.quantity;
              // leave currencyTotal unchanged since that is lifetime currency earned, not current balance
            } else if (d.type === 'itemRemoved') {
              if (tempPlayer.items && tempPlayer.items[d.id]) {
                tempPlayer.items[d.id].count += d.quantity;
              }
            }
          }
        }
      } else {
        for (let d of stuff.data) {
          if (d.type === 'xp' && tempPlayer.points > 0) {
            tempPlayer.points -= d.quantity;
          } else if (d.type === 'currency' && tempPlayer.currencyTotal > 0) {
            tempPlayer.currency -= d.quantity;
            tempPlayer.currencyTotal -= d.quantity;
          } else if (d.type === 'item') {
            tempPlayer.items[d.id].count -= d.quantity;
          } else if (d.type === 'badge') {
            tempPlayer.badges[d.id].count -= d.quantity;
          }
        }
      }
    }
    return tempPlayer;
  }

  function updatePlayerData(data) {
    setPlayerData(data);
  }


  // Built once so every breakpoint renders the same content with the same props.
  const gameContent = (
    <PlayerGameContent
      gameData={gameData}
      playerData={playerData}
      updatePlayerData={updatePlayerData}
      gameElements={gameElements}
      actionItems={actionItems}
      gameStats={gameStats}
      playerList={playerList}
      teamMembers={teamMembers}
      myTeams={myTeams}
      latestMessage={latestMessage}
      unadjustedPlayerData={unadjustedPlayerData}
    />
  );

  return (
    <div>
      {
        (gameData && playerData)
          ?
          <div className='play-game-page' style={
            (gameData.bg.type === 'gradient')
              ?
              { 'background': `linear-gradient(to bottom, ${gameData.bg.hexTop}, ${gameData.bg.hexBottom})` }
              :
              { 'backgroundImage': `url("${gameData.bg.url}")` } // no-repeat center center fixed
          }>
            {
              (useMobileHeadBar)
                ?
                <PlayHeadBarMobile
                  gameData={gameData}
                  playerData={playerData}
                  gameElements={gameElements}
                  teamMembers={teamMembers} />
                :
                <PlayHeadBar
                  gameData={gameData}
                  playerData={playerData}
                  playerActivity={playerActivity}
                  gameElements={gameElements} />
            }
            {
              (isDesktop)
                ?
                <div className='play-game-container'>
                  <div className='play-game-content'>
                    {gameContent}
                  </div>
                </div>
                :
                gameContent
            }
          </div>
          :
          (loadError)
            ?
            <div className='loading-page'>
              <div className='g-card load-error-card'>
                <div className='card-title'>We could not load this game</div>
                <div className='g-space-1'></div>
                <div>{loadError}</div>
                <div className='g-space-1'></div>
                <div className='buttons'>
                  <button
                    className='g-button primary'
                    onClick={() => window.location.reload()}>
                    Try Again
                  </button>
                  <button
                    className='g-button'
                    onClick={() => navigate('/')}>
                    My Games
                  </button>
                </div>
              </div>
            </div>
            :
            <div className='loading-page'>
              <Loader />
            </div>
      }
      <LegalApprovalModal
        playerData={playerData} />
      <AnnouncementModal />
    </div>
  )
}

export default PlayGamePage